import React, { useEffect, useState } from "react";
import CloudBackground from "../community/shared/components/CloudBackground";
import PButton from "../../ui/input/PButton";
import { verifyGiftSession } from "../../../api/gift_stripe";

type GiftCheckoutSuccessProps = {
  sessionId?: string;
  onBackToHome?: () => void;
  homeUrl?: string;
};

const GiftCheckoutSuccess: React.FC<GiftCheckoutSuccessProps> = ({
  sessionId,
  onBackToHome,
  homeUrl = "/",
}) => {
  const [paymentStatus, setPaymentStatus] = useState<
    "loading" | "success" | "error"
  >("loading");
  const [sessionInfo, setSessionInfo] = useState<{
    amount?: number;
    currency?: string;
    customerEmail?: string;
  }>({});

  // Add custom fonts for seriously nostalgic
  useEffect(() => {
    const preloadLink = document.createElement("link");
    preloadLink.rel = "preload";
    preloadLink.href = "/assets/fonts/SeriouslyNostalgicFn-Regular.otf";
    preloadLink.as = "font";
    preloadLink.type = "font/otf";
    preloadLink.crossOrigin = "anonymous";
    document.head.appendChild(preloadLink);

    const style = document.createElement("style");
    style.textContent = `
      @font-face {
        font-family: 'Seriously Nostalgic';
        src: url('/assets/fonts/SeriouslyNostalgicFn-Regular.otf') format('opentype');
        font-display: swap;
        font-weight: normal;
        font-style: normal;
      }
      
      .font-seriously-nostalgic { 
        font-family: 'Seriously Nostalgic', 'Playfair Display', 'Times New Roman', serif !important;
        font-weight: normal;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      if (document.head.contains(preloadLink)) {
        document.head.removeChild(preloadLink);
      }
    };
  }, []);

  useEffect(() => {
    // Clear gift personalization form history on checkout completion
    const clearFormHistory = () => {
      try {
        localStorage.removeItem("giftPersonalizationForm");
      } catch (error) {
        // Silently fail if localStorage is not available
      }
    };

    const verifySession = async () => {
      if (!sessionId) {
        setPaymentStatus("error");
        clearFormHistory();
        return;
      }

      try {
        const result = await verifyGiftSession(sessionId);

        if (
          result.success &&
          result.status === "complete" &&
          result.payment_status === "paid"
        ) {
          setPaymentStatus("success");
          setSessionInfo({
            amount: result.amount_total ? result.amount_total / 100 : undefined, // Convert cents to dollars
            currency: result.currency || undefined,
            customerEmail: result.customer_email || undefined,
          });
        } else {
          setPaymentStatus("error");
        }
        // Clear form history regardless of payment status
        clearFormHistory();
      } catch (error) {
        setPaymentStatus("error");
        clearFormHistory();
      }
    };

    verifySession();
  }, [sessionId]);

  if (paymentStatus === "loading") {
    return (
      <CloudBackground>
        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
          <div className="text-center max-w-2xl mx-auto">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-8" />
            <h1 className="font-seriously-nostalgic text-[40px] sm:text-[50px] md:text-[60px] text-white mb-4 leading-tight">
              Processing your gift...
            </h1>
            <p className="text-white/85 text-lg leading-relaxed font-light">
              Please wait while we confirm your payment.
            </p>
          </div>
        </div>
      </CloudBackground>
    );
  }

  if (paymentStatus === "error") {
    return (
      <CloudBackground>
        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-6xl mb-8">😔</div>
            <h1 className="font-seriously-nostalgic text-[44px] sm:text-[52px] md:text-[64px] text-white mb-6 leading-tight">
              Payment Error
            </h1>
            <p className="text-white/80 mb-8 leading-relaxed font-light">
              Something went wrong with your payment. Please try again.
            </p>
            <PButton
              onClick={onBackToHome}
              href={homeUrl}
              kind="primary"
              className="!bg-black hover:!bg-[#111111] !text-white !border-black px-12 py-4 text-lg font-medium rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              Try Again
            </PButton>
          </div>
        </div>
      </CloudBackground>
    );
  }

  // Success state
  return (
    <CloudBackground>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
        <div className="text-center max-w-3xl mx-auto">
          {/* Success Icon */}
          <div className="text-8xl mb-8">🎁</div>

          {/* Main Heading */}
          <h1 className="font-seriously-nostalgic text-[44px] sm:text-[54px] md:text-[64px] text-white mb-6 leading-tight">
            Gift sent successfully!
          </h1>

          {/* Success Message */}
          <div className="bg-white/15 backdrop-blur-xl rounded-3xl p-8 mb-8 border border-white/30 shadow-2xl">
            <p className="text-white text-lg leading-relaxed font-light mb-4">
              Your gift has been purchased and will be delivered shortly!
            </p>

            {/* Payment Details */}
            {sessionInfo.amount && (
              <div className="mb-4 p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <p className="text-white/80 text-sm mb-2">Payment Confirmed:</p>
                <p className="text-white font-medium">
                  ${sessionInfo.amount.toFixed(2)}{" "}
                  {sessionInfo.currency?.toUpperCase() || "USD"}
                </p>
                {sessionInfo.customerEmail && (
                  <p className="text-white/75 text-sm mt-1">
                    Receipt sent to: {sessionInfo.customerEmail}
                  </p>
                )}
              </div>
            )}

            <p className="text-white/85 leading-relaxed">
              The recipient will receive an email with instructions on how to
              redeem their setups.
              <br />
              <br />
              Thank you for spreading the love and helping someone find their
              person! ❤️
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <PButton
              onClick={onBackToHome}
              href={homeUrl}
              kind="primary"
              className="!bg-black hover:!bg-[#111111] !text-white !border-black px-12 py-4 text-lg font-medium rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              Back to Home
            </PButton>
          </div>
        </div>
      </div>
    </CloudBackground>
  );
};

GiftCheckoutSuccess.defaultProps = {
  sessionId: undefined,
  onBackToHome: undefined,
  homeUrl: "/",
};

export default GiftCheckoutSuccess;
